/**
 * TargetsDataProvider - Listens for targets data requests from popups
 *
 * This component runs in the main window and handles POPUP_INIT requests
 * for 'targets' type by fetching ALL targets (not paginated) and responding.
 *
 * This ensures that popup charts and tables have access to the complete dataset,
 * not just the current page displayed in the main window.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { getTargetList, useSession } from '@ais-ui/websockets';
import {
  usePopupMessages,
  MainWindowActions,
  MessageType,
  PopupMessage,
  PopupInitPayload,
} from './PopupEvents';
import { PopupType } from '@ais-ui/popups';
import { useSettings } from '../Settings/SettingsProvider';

interface ActiveRequest {
  popupId: string;
  bounds: {
    minLat: number;
    maxLat: number;
    minLon: number;
    maxLon: number;
  };
}

export const TargetsDataProvider = (): null => {
  const [activeRequest, setActiveRequest] = useState<ActiveRequest | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const { olSession } = useSession();
  const { settings } = useSettings();
  const settingsRef = useRef(settings);

  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  // Fetch all targets when we have an active request
  useEffect(() => {
    if (!activeRequest || isLoading || !olSession) return;

    const fetchAllTargets = async () => {
      setIsLoading(true);
      const { popupId, bounds } = activeRequest;

      try {
        // First, get the total count with a small request
        const initialResponse = await getTargetList(olSession, {
          minLat: bounds.minLat,
          maxLat: bounds.maxLat,
          minLon: bounds.minLon,
          maxLon: bounds.maxLon,
          startIndex: 0,
          targetsPerPage: 1,
          orderBy: 'name',
          ascending: true,
        });

        const totalTargets = initialResponse.totalShips;

        // Now fetch ALL targets in one request
        const fullResponse = await getTargetList(olSession, {
          minLat: bounds.minLat,
          maxLat: bounds.maxLat,
          minLon: bounds.minLon,
          maxLon: bounds.maxLon,
          startIndex: 0,
          targetsPerPage: totalTargets || 10000, // Fetch all, fallback to large number
          orderBy: 'name',
          ascending: true,
        });

        const tableDensity =
          settingsRef.current['ribbonPlugin.settings']?.table_density ||
          'compact';

        // Generate dataKey based on targets content to prevent unnecessary re-renders
        const firstMmsi = fullResponse.targets[0]?.mmsi || '';
        const lastMmsi =
          fullResponse.targets[fullResponse.targets.length - 1]?.mmsi || '';
        const dataKey = `${bounds.minLat}_${bounds.maxLat}_${fullResponse.totalShips}_${firstMmsi}_${lastMmsi}`;

        MainWindowActions.sendInitResponse(popupId, {
          targets: fullResponse.targets,
          total: fullResponse.totalShips,
          retrievalTime: fullResponse.timeStamp,
          bounds,
          settings: { tableDensity },
          dataKey,
        });
      } catch (error) {
        console.error('[TargetsDataProvider] Error fetching targets:', error);
        MainWindowActions.sendInitResponse(popupId, {
          targets: [],
          total: 0,
          retrievalTime: new Date().toISOString(),
          bounds,
          error: 'Failed to fetch targets data',
        });
      } finally {
        setIsLoading(false);
        setActiveRequest(null);
      }
    };

    fetchAllTargets();
  }, [activeRequest, isLoading, olSession]);

  // Listen for targets popup init requests
  usePopupMessages(
    useCallback((msg: PopupMessage) => {
      if (msg.type === MessageType.POPUP_INIT) {
        const payload = msg.payload as PopupInitPayload;

        if (payload.popupType === PopupType.Targets) {
          const params = payload.params as {
            minLat?: string;
            maxLat?: string;
            minLon?: string;
            maxLon?: string;
          };

          const bounds = {
            minLat: Number(params.minLat) || 0,
            maxLat: Number(params.maxLat) || 0,
            minLon: Number(params.minLon) || 0,
            maxLon: Number(params.maxLon) || 0,
          };

          setActiveRequest({
            popupId: payload.popupId,
            bounds,
          });
        }
      }
    }, []),
    [],
  );

  return null;
};
